import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export type UserNotificationSeverity = 'info' | 'warning' | 'error' | 'success';

export type UserCommunicationNotification = {
    id: string;
    notificationId: string;
    title: string;
    message: string;
    severity: UserNotificationSeverity;
    isActive: boolean;
    priority: number;
    startsAt: string | null;
    endsAt: string | null;
    ctaLabel: string;
    ctaUrl: string;
    couponCode: string;
    createdAt: string;
    updatedAt: string;
};

export type UserCommunicationNotificationDraft = {
    title: string;
    message: string;
    severity: UserNotificationSeverity;
    isActive: boolean;
    priority: number;
    startsAt: string;
    endsAt: string;
    ctaLabel: string;
    ctaUrl: string;
    couponCode: string;
};

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

type AdminNotificationListResponse = {
    page: number;
    pageSize: number;
    totalCount: number;
    items: UserCommunicationNotification[];
};

type MutationInput = {
    title?: string;
    message: string;
    severity: UserNotificationSeverity;
    isActive: boolean;
    priority: number;
    startsAt?: string | null;
    endsAt?: string | null;
    ctaLabel?: string;
    ctaUrl?: string;
    couponCode?: string;
};

export function createEmptyNotificationDraft(): UserCommunicationNotificationDraft {
    return {
        title: '',
        message: '',
        severity: 'info',
        isActive: false,
        priority: 0,
        startsAt: '',
        endsAt: '',
        ctaLabel: '',
        ctaUrl: '',
        couponCode: '',
    };
}

export function createNotificationDraft(notification: UserCommunicationNotification): UserCommunicationNotificationDraft {
    return {
        title: notification.title,
        message: notification.message,
        severity: notification.severity,
        isActive: notification.isActive,
        priority: notification.priority,
        startsAt: toDateTimeLocalValue(notification.startsAt),
        endsAt: toDateTimeLocalValue(notification.endsAt),
        ctaLabel: notification.ctaLabel,
        ctaUrl: notification.ctaUrl,
        couponCode: notification.couponCode,
    };
}

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;
    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin notification request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asNullableString(value: unknown): string | null {
    return typeof value === 'string' && value ? value : null;
}

function asNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
}

function asSeverity(value: unknown): UserNotificationSeverity {
    return value === 'warning' || value === 'error' || value === 'success' ? value : 'info';
}

function normalizeNotification(value: unknown): UserCommunicationNotification {
    const record = asRecord(value);
    const notificationId = asString(record.notificationId);

    return {
        id: notificationId,
        notificationId,
        title: asString(record.title),
        message: asString(record.message),
        severity: asSeverity(record.severity),
        isActive: asBoolean(record.isActive, false),
        priority: asNumber(record.priority, 0),
        startsAt: asNullableString(record.startsAt),
        endsAt: asNullableString(record.endsAt),
        ctaLabel: asString(record.ctaLabel),
        ctaUrl: asString(record.ctaUrl),
        couponCode: asString(record.couponCode),
        createdAt: asString(record.createdAt),
        updatedAt: asString(record.updatedAt),
    };
}

function normalizeNotificationListResponse(value: unknown): AdminNotificationListResponse {
    const payload = unwrapAdminApiPayload(value as AdminNotificationListResponse | AdminApiEnvelope<AdminNotificationListResponse>);
    const record = asRecord(payload);
    const items = Array.isArray(record.items) ? record.items.map(normalizeNotification) : [];

    return {
        page: asNumber(record.page, 1),
        pageSize: asNumber(record.pageSize, items.length || 1),
        totalCount: asNumber(record.totalCount, items.length),
        items,
    };
}

async function requestAdminNotifications<T>(
    path: string,
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const resourcePath = trimSlashes(path);
    const url = new URL(
        resourcePath
            ? `${apiRoot}/AdminUserCommunicationNotifications/${resourcePath}`
            : `${apiRoot}/AdminUserCommunicationNotifications`,
    );

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin notification request failed'));
        }
    }

    return payload as T;
}

function toApiDate(value: string): string | null {
    if (!value.trim()) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function toDateTimeLocalValue(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const offsetMs = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toMutationBody(input: UserCommunicationNotificationDraft): MutationInput {
    return {
        title: input.title.trim() || undefined,
        message: input.message.trim(),
        severity: input.severity,
        isActive: input.isActive,
        priority: Number.isFinite(input.priority) ? input.priority : 0,
        startsAt: toApiDate(input.startsAt),
        endsAt: toApiDate(input.endsAt),
        ctaLabel: input.ctaLabel.trim() || undefined,
        ctaUrl: input.ctaUrl.trim() || undefined,
        couponCode: input.couponCode.trim() || undefined,
    };
}

export async function getAdminNotificationList(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    includeInactive?: boolean;
}): Promise<AdminNotificationListResponse> {
    const response = await requestAdminNotifications<unknown>('', { method: 'GET' }, {
        page: String(params?.page ?? 1),
        pageSize: String(params?.pageSize ?? 100),
        search: params?.search?.trim() ?? '',
        includeInactive: String(params?.includeInactive ?? true),
    });

    return normalizeNotificationListResponse(response);
}

export async function createAdminNotification(input: UserCommunicationNotificationDraft): Promise<UserCommunicationNotification> {
    const response = await requestAdminNotifications<unknown>('', {
        method: 'POST',
        body: JSON.stringify(toMutationBody(input)),
    });

    return normalizeNotification(unwrapAdminApiPayload(response as UserCommunicationNotification | AdminApiEnvelope<UserCommunicationNotification>));
}

export async function updateAdminNotification(
    notificationId: string,
    input: UserCommunicationNotificationDraft,
): Promise<UserCommunicationNotification> {
    const response = await requestAdminNotifications<unknown>(notificationId, {
        method: 'PUT',
        body: JSON.stringify(toMutationBody(input)),
    });

    return normalizeNotification(unwrapAdminApiPayload(response as UserCommunicationNotification | AdminApiEnvelope<UserCommunicationNotification>));
}

export async function deleteAdminNotification(notificationId: string): Promise<void> {
    await requestAdminNotifications(notificationId, {
        method: 'DELETE',
    });
}
